import { runtimeConfig } from "../../config/runtime";
import { HomeAudio } from "../../types/ScreenTypes";
import { partnerStartsItself } from "./homeSettings";

// The stream box rewrites live.json every second; older than this means it has stopped, so it says nothing.
const STALE_SECONDS = 60;

export interface LiveStatus {
  // Slugs of the Masjid 24/7 streams live now.
  slugs: Set<string>;
  // YouTube videos live now, by masjid id (the stream box checks masjids with YouTube links).
  video: Record<string, string>;
  // Partner masjids live now, by masjid id: provider (eMasjid family and Mixlr; MyMasjid has no feed).
  // null when the box couldn't read every partner listing just now: ask the backend instead.
  partners: Record<string, string> | null;
}

// From the stream box's live.json, or null when unknown (offline, or the file is stale).
export const fetchLiveStatus = async (liveStatusUrl: string): Promise<LiveStatus | null> => {
  try {
    const response = await fetch(liveStatusUrl, { cache: "no-store" });
    const data: {
      t: number;
      live: string[];
      video?: Record<string, string>;
      partners?: Record<string, string>;
    } = await response.json();
    if (Date.now() / 1000 - data.t > STALE_SECONDS) return null;
    return { slugs: new Set(data.live), video: data.video ?? {}, partners: data.partners ?? null };
  } catch {
    return null;
  }
};

export const isOwnStreamLive = (audio: HomeAudio | undefined, slugs: Set<string>): boolean =>
  !!audio?.liveStreams.some((stream) => slugs.has(stream.slug));

export const checkStatusUrl = (partner: { provider: string; url: string }): string =>
  `${runtimeConfig.digitalSignageApiDomain}/api/Masjid/CheckStatus?${new URLSearchParams({
    provider: partner.provider,
    url: partner.url,
  })}`;

// Partner services (eMasjidLive, MyMasjid, Mixlr) through the backend's cached status check, or
// null when unknown: a failed check mustn't close a player that started itself.
export const checkPartnerLive = async (partner: {
  provider: string;
  url: string;
}): Promise<boolean | null> => {
  try {
    const response = await fetch(checkStatusUrl(partner));
    const data = await response.json();
    return (data?.data ?? data)?.isLive === true;
  } catch {
    return null;
  }
};

// The hash the app looks for before pressing play inside a partner's player (MainActivity).
export const PARTNER_AUTOPLAY_HASH = "#m247-autoplay";

// The partner's own player, the same pages the website opens.
export const partnerPlayerUrl = (partner: { provider: string; url: string }): string => {
  const parsed = new URL(partner.url);
  const parts = parsed.pathname.split("/").filter(Boolean);
  if (partner.provider === "mixlr") return `https://mixlr.com/${parts[0]}/embed`;
  const channel = parts[parts.length - 1];
  const host = partner.provider === "mymasjid" ? "mymasjid.uk" : parsed.hostname;
  const autoplay = partnerStartsItself(partner.provider) ? PARTNER_AUTOPLAY_HASH : "";
  return `https://${host}/miniplayer/${channel}?theme=dark${autoplay}`;
};
